/**
 * Content Labels v2 — the vocabulary a per-category table column is built from.
 *
 * It lives in `src/utils/` rather than beside either table because BOTH tables
 * need it: the Settings stage table (`routes/settings/labels`) captions its
 * trailing column with `labelColumnLabel`, and the portfolio Content tab
 * (`routes/portfolio`) captions one column per category with the very same
 * string — plus `components-ui/RowLabelPicker`, which may not import from
 * `routes/` at all.
 *
 * Nothing here imports the settings `LabelCategory`: the parameter is typed
 * structurally (`{ name: string }`), to which a `LabelCategory` is assignable,
 * so `src/utils/` gains no dependency on a route folder.
 */

/** The only field of a category this module reads. */
export type NamedCategory = { name: string };

/**
 * The category-named noun, used BOTH as a column's caption and as the row
 * control's accessible name — "Country label", "Change Country label for …".
 *
 * ONE HELPER FOR THE TWO, because `12466:44883` captions the column "country
 * label" (the category name plus " label", uppercased by CSS) and the row
 * control has always needed the same words: out of context, "Change Label" says
 * nothing about which dimension is being edited. The older frames that printed a
 * generic "Label" in the header are settled by `12466-44703` — Figma-exact,
 * chosen 2026-09-14.
 *
 * An explicit `override` wins; a nameless category degrades to a bare "Label"
 * rather than a dangling " label".
 */
export function labelColumnLabel(category: NamedCategory, override?: string): string {
  const explicit = override?.trim();
  if (explicit) return explicit;
  const name = category?.name?.trim();
  return name ? `${name} label` : 'Label';
}

/**
 * The prefix every content-label column id carries, so a consumer can tell one
 * apart from the table's fixed columns without knowing the team's categories.
 */
export const CONTENT_LABEL_COLUMN_PREFIX = 'contentLabel_';

/**
 * The react-table column id for one category.
 *
 * An UNDERSCORE, not a dot or a bracket: react-table resolves a string accessor
 * as a lodash path, so `contentLabel.<uuid>` would look for a nested field and
 * read `undefined` on every row. The columns hand react-table a FUNCTION
 * accessor so the path is never parsed at all, but the id itself still travels
 * through `TableManager`, the sticky-column list and the hidden-column store as
 * a plain string — this keeps it inert wherever it lands.
 *
 * A category uuid is `[0-9a-f-]`, so the prefix cannot collide with a fixed
 * column ('content', 'label', 'impactGroup', `funnel_<id>`).
 */
export function contentLabelColumnId(contentLabelCategoryId: string): string {
  return `${CONTENT_LABEL_COLUMN_PREFIX}${contentLabelCategoryId}`;
}
